<!--------------- Dialog header ----------------->
<div class="modal__head">
  <div class="grid-row grid-row--aic">
    <div class="grid-col">
      <h2>{{ statementRecipientHeader }}s excluded from receiving statements</h2>
    </div>
    <div class="grid-col grid-col--auto">
      <button type="button" class="modal__close" (click)="onClose()">
        <anduin-icon name="x" aria-hidden="true" height="24" stroke="currentColor" stroke-width="2" width="24" />
      </button>
    </div>
  </div>
</div>

<!--------------- Dialog content ----------------->
<div *ngIf="contentState$ | async as contentState" class="modal__cont">
  <div *ngIf="errorOnLatestRequest$ | async as requestError" class="alert alert--error mb--150">
    {{ requestError }}
  </div>

  <p class="mb--150 call-to-action">Select which {{ statementRecipientLabel }}s to exclude from receiving any statements via Aiwyn.</p>

  <!-- Search input -->
  <div class="search-input-container">
    <div class="grid-row grid-row--smg grid-row--aib">
      <div class="grid-col">
        <input
          type="text"
          class="form-input"
          [formControl]="recipientSearch"
          [placeholder]="
            'Search ' +
            (configService.collectionsMode === CollectionsMode.CLIENT ? 'client name or number' : 'billing contact email') +
            ' (two character minimum)'
          "
        />
      </div>
    </div>
  </div>

  <!-- Client Table -->
  <div class="table-wrapper">
    <table
      class="data-table__tbl"
      [dataSource]="contentState.isLoading && contentState.source === ClientSource.SEARCH ? [] : dataSource"
      mat-table
      matSort
    >
      <!--------------- Table cell definitions ----------------->

      <!-- Exclusion status checkbox -->
      <ng-container matColumnDef="exclusionSelection">
        <th *matHeaderCellDef mat-header-cell>
          <mat-checkbox
            *ngIf="headerCheckboxStatus$ | async as headerCheckboxStatus"
            [attr.aria-label]="
              headerCheckboxStatus === HeaderCheckboxStatus.UNCHECKED ? 'Exclude ' : 'Include ' + 'all ' + statementRecipientLabel + 's'
            "
            [checked]="headerCheckboxStatus === HeaderCheckboxStatus.CHECKED"
            [indeterminate]="headerCheckboxStatus === HeaderCheckboxStatus.INDETERMINATE"
            (change)="onHeaderCheckboxChange($event.checked)"
          />
        </th>

        <td *matCellDef="let recipient" mat-cell>
          <mat-checkbox
            [attr.aria-label]="(recipient.excludeFromCollections ?? !recipient.enabled) ? 'Include ' : 'Exclude ' + recipient.name"
            [attr.id]="recipient.clientRef ?? recipient.id"
            [checked]="recipient.excludeFromCollections ?? !recipient.enabled"
          />
        </td>
      </ng-container>

      <!-- Client ID (ref) -->
      <ng-container matColumnDef="clientRef">
        <th *matHeaderCellDef mat-header-cell mat-sort-header>CLIENT ID</th>
        <td *matCellDef="let recipient" mat-cell>{{ recipient.clientRef }}</td>
      </ng-container>

      <!-- Client name -->
      <ng-container matColumnDef="name">
        <th *matHeaderCellDef class="w-100" mat-header-cell mat-sort-header>{{ recipientColumnLabel }}</th>
        <td *matCellDef="let recipient" class="w-100" mat-cell>{{ recipient.name }}</td>
      </ng-container>

      <!-- Staff name that last updated client's exclusion status -->
      <ng-container matColumnDef="updateByStaffName">
        <th *matHeaderCellDef mat-header-cell>UPDATED BY</th>
        <td *matCellDef="let recipient" mat-cell>{{ recipient.updateByStaffName ?? recipient.enabledUpdatedByStaff?.name }}</td>
      </ng-container>

      <!-- Date at which client's exclusion status was last updated -->
      <ng-container matColumnDef="exclusionStatusUpdatedAt">
        <th *matHeaderCellDef mat-header-cell mat-sort-header>UPDATED</th>
        <td *matCellDef="let recipient" mat-cell>
          {{ recipient.exclusionStatusUpdatedAt ?? recipient.enabledUpdatedAt | date: 'MM/dd/YYYY' }}
        </td>
      </ng-container>

      <!--------------- Table row definitions ----------------->
      <tr *matHeaderRowDef="displayedColumns" class="data-table__row header-row" mat-row></tr>
      <tr
        *matRowDef="let recipient; let i = index; columns: displayedColumns"
        class="data-table__row data-table__row--selectable data-table__row--clickable mat-checkbox-container"
        (click)="onCellCheckboxChange(recipient)"
        mat-row
      ></tr>
    </table>

    <!--------------- Under table content ----------------->
    <ng-container [ngSwitch]="contentState.underTableContent">
      <!-- Spinner -->
      <div *ngSwitchCase="UnderTableContent.LOADING" class="loading-pane">
        <mat-spinner />
      </div>

      <!-- Page zero state -->
      <div *ngSwitchCase="UnderTableContent.PAGE_ZERO_STATE" class="empty-state-box">
        <div class="empty-state-box__text">
          No {{ tableStateRecipientLabel }}s are excluded. Search for a {{ statementRecipientLabel }} to exclude them.
        </div>
      </div>

      <!-- Search zero state -->
      <div *ngSwitchCase="UnderTableContent.SEARCH_ZERO_STATE" class="empty-state-box">
        <div class="empty-state-box__text">No {{ tableStateRecipientLabel }}s match the search term.</div>
      </div>

      <!-- Page results are complete -->
      <div *ngSwitchCase="UnderTableContent.HAS_ALL_RESULTS" class="d-flex justify-content-center my-4">
        <p>No additional excluded {{ tableStateRecipientLabel }}s</p>
      </div>

      <!-- Page results are incomplete -->
      <div *ngSwitchCase="UnderTableContent.LOAD_MORE_BUTTON" class="d-flex justify-content-center my-4">
        <button class="button button--text" (click)="onLoadMoreButtonClick()">Load more</button>
      </div>
    </ng-container>
  </div>
</div>

<!--------------- Footer ----------------->
<div class="modal__footer">
  <!-- Cancel -->
  <button class="button" (click)="onClose()">Cancel</button>

  <div class="count-and-submit">
    <!-- Number of excluded recipients (including current updates) -->
    <p *ngIf="numberOfExcludedRecipients$ | async as numberOfExcludedRecipients" class="number-of-excluded-recipients">
      {{ numberOfExcludedRecipients.amount }} {{ statementRecipientLabel }}{{ numberOfExcludedRecipients.amount === 1 ? '' : 's' }} excluded
    </p>

    <!-- Submit -->
    <ng-container *ngIf="onSubmit$ | async as updateStatus">
      <button type="button" [disabled]="!currentUpdates$.value.length" (click)="onSubmit()" andButton color="primary">
        <ng-container *ngIf="updateStatus === LoadableStatus.LOADING; else updateExcludedRecipientsTemplate">
          Updating
          <mat-spinner class="button-update-spinner" diameter="16" />
        </ng-container>

        <ng-template #updateExcludedRecipientsTemplate> Update Excluded {{ statementRecipientLabel | titlecase }}s </ng-template>
      </button>
    </ng-container>
  </div>
</div>
